import { useState } from 'react';
import { useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import { Button, Container, Grid, Typography, FormControl, InputLabel, Select, MenuItem, FormHelperText, TextField } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { ErrorIcon } from '../../../icons/ErrorIcon';
import { CALL_SIMULATION_DURATIONS, CallSimulationDurationKey } from '../../../constants';
import Hello from './Hello.png';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridContainer: {
      justifyContent: 'space-between',
    },
    /* 
    The size of the image is explicitly stated here so that this content can properly be centered vertically
    before the image is loaded.
    */
    illustration: {
      width: '284px',
      height: '284px',
      [theme.breakpoints.down('md')]: {
        width: '200px',
        height: '200px',
      },
    },
    emailField: {
      minWidth: 300,
      marginBottom: '16px',
    },
    formControl: {
      minWidth: 300,
      marginBottom: '10px',
    },
    helpText: {
      marginBottom: '20px',
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
    buttonContainer: {
      marginTop: theme.spacing(1),
    },
  })
);

export function GetStarted() {
  const { nextPane, state, dispatch } = useAppStateContext();
  const classes = useStyles();
  const [emailError, setEmailError] = useState<string | null>(null);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const email = event.target.value;
    dispatch({ type: 'set-customer-email', email });
    if (emailError && (email === '' || isValidEmail(email))) {
      setEmailError(null);
    }
  };

  const handleEmailBlur = () => {
    if (state.customerEmail && !isValidEmail(state.customerEmail)) {
      setEmailError('Please enter a valid email address');
    }
  };

  const handleDurationChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedDurationKey = event.target.value as CallSimulationDurationKey;
    const duration = CALL_SIMULATION_DURATIONS[selectedDurationKey];
    dispatch({ type: 'set-call-simulation-duration', duration });
  };

  const handleGetStartedClick = () => {
    if (state.customerEmail && !isValidEmail(state.customerEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (state.selectedCallSimulationDuration === null) {
      dispatch({ type: 'set-duration-selection-error', error: 'Please select how long the test should run' });
      return;
    }
    nextPane();
  };

  const getSelectedDurationKey = (): string => {
    const duration = state.selectedCallSimulationDuration;
    if (duration === null) return '';
    
    const entry = Object.entries(CALL_SIMULATION_DURATIONS).find(([, value]) => value === duration);
    return entry ? entry[0] : '';
  };

  return (
    <Container>
      <Grid container alignItems="center" className={classes.gridContainer}>
        <Grid item lg={5}>
          {state.appIsExpired ? (
            <>
              <Typography variant="h1" gutterBottom>
                <ErrorIcon />
                App has expired.
              </Typography>

              <Typography variant="body1" gutterBottom>
                Please re-deploy the application and try again.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h1" gutterBottom>
                CloudTalk<br/>Network Diagnostics
              </Typography>

              <Typography variant="body1" gutterBottom>
                We'll run a quick check to make sure you get the best possible call quality.
              </Typography>
              
              <TextField
                className={classes.emailField}
                label="Customer email (recommended)"
                placeholder="customer@example.com"
                type="email"
                value={state.customerEmail}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                error={!!emailError}
                helperText={emailError || 'Helps identify the report later'}
                InputLabelProps={{ shrink: true }}
              />

              <FormControl className={classes.formControl} error={!!state.durationSelectionError}>
                <InputLabel id="duration-select-label">Pick how long the test will run</InputLabel>
                <Select
                  labelId="duration-select-label"
                  id="duration-select"
                  value={getSelectedDurationKey()}
                  onChange={handleDurationChange}
                >
                  {Object.keys(CALL_SIMULATION_DURATIONS).map((durationKey) => (
                    <MenuItem key={durationKey} value={durationKey}>
                      {durationKey}
                    </MenuItem>
                  ))}
                </Select>
                {state.durationSelectionError && (
                  <FormHelperText>{state.durationSelectionError}</FormHelperText>
                )}
              </FormControl>
              
              <Typography variant="body2" className={classes.helpText}>
                We recommend at least 10 minutes for the most comprehensive results.
              </Typography>
            </>
          )}

          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGetStartedClick}
              style={{ marginBottom: '1em' }}
              disabled={state.appIsExpired}
            >
              Start network test
            </Button>
          </div>
        </Grid>

        <Grid item lg={5}>
          <img src={Hello} alt="Hello" className={classes.illustration} />
        </Grid>
      </Grid>
    </Container>
  );
}
